import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from "react";

import {
  getMyProjects,
  getProject,
  createProject,
  updateProject,
  deleteProject,
} from "../services/projectService";

import { useAuth } from "./AuthContext";
import { useCompany } from "./CompanyContext";

/* ===================================================== */
/* Context                                               */
/* ===================================================== */

const ProjectContext = createContext(null);

/* ===================================================== */
/* Provider                                              */
/* ===================================================== */

export const ProjectProvider = ({ children }) => {
  const { isLoggedIn } = useAuth();
  const { companyId } = useCompany();

  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [loading, setLoading] = useState(true);

  /* ===================================================== */
  /* 🧠 Storage Key (Company Scoped)                       */
  /* ===================================================== */

  const storageKey = useMemo(
    () => `hs_lastProjectId_${companyId || "global"}`,
    [companyId]
  );

  /* ===================================================== */
  /* 📦 Load Projects                                     */
  /* ===================================================== */

  const loadProjects = useCallback(async () => {
    if (!isLoggedIn || !companyId) {
      setProjects([]);
      setCurrentProject(null);
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);

    try {
      const data = await getMyProjects();
      const list = Array.isArray(data) ? data : [];

      if (!active) return;

      setProjects(list);

      /* Restore last selected project */
      const savedId = localStorage.getItem(storageKey);

      const selected =
        list.find((p) => p._id === savedId) ||
        list[0] ||
        null;

      setCurrentProject(selected);
    } catch (err) {
      if (!active) return;

      console.error("❌ Failed to load projects:", err.message);

      setProjects([]);
      setCurrentProject(null);
    } finally {
      if (active) setLoading(false);
    }

    return () => {
      active = false;
    };
  }, [isLoggedIn, companyId, storageKey]);

  /* ===================================================== */
  /* 🚀 Auto Load                                         */
  /* ===================================================== */

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  /* ===================================================== */
  /* 🎯 Select Project                                    */
  /* ===================================================== */

const selectProject = useCallback(
  async (projectId) => {
    if (!projectId) return;

    try {
      const project = await getProject(projectId);

      /* ===================================================== */
      /* 🧠 NORMALIZE PROJECT (CRITICAL)                       */
      /* ===================================================== */

      const enriched = {
        ...project,

        // 🔥 SINGLE SOURCE OF TRUTH (from backend)
        projectPlan: project.plan || "free",

        // 🔐 PERMISSION CONTEXT
        isOwnerCompany:
          String(project.clientCompanyId) === String(companyId),

        isContractorCompany:
          String(project.contractorCompanyId) === String(companyId),
      };

      /* ===================================================== */
      /* 💾 STATE UPDATE                                       */
      /* ===================================================== */

      setCurrentProject(enriched);

      /* ===================================================== */
      /* 🧠 PERSIST LAST PROJECT                               */
      /* ===================================================== */

      localStorage.setItem(storageKey, projectId);

      return enriched;
    } catch (err) {
      console.error("❌ Failed to select project:", err.message);
      throw err;
    }
  },
  [storageKey, companyId]
);

  /* ===================================================== */
  /* ➕ Create Project                                    */
  /* ===================================================== */

  const addProject = useCallback(
    async (payload) => {
      const created = await createProject(payload);

      setProjects((prev) => [created, ...prev]);
      setCurrentProject(created);

      localStorage.setItem(storageKey, created._id);

      return created;
    },
    [storageKey]
  );

  /* ===================================================== */
  /* ✏️ Update Project                                    */
  /* ===================================================== */

  const editProject = useCallback(async (id, updates) => {
    const updated = await updateProject(id, updates);

    setProjects((prev) =>
      prev.map((p) =>
        p._id === id ? { ...p, ...updated } : p
      )
    );

    setCurrentProject((prev) =>
      prev?._id === id ? updated : prev
    );

    return updated;
  }, []);

  /* ===================================================== */
  /* 🗑 Delete Project                                    */
  /* ===================================================== */

  const removeProject = useCallback(
    async (id) => {
      await deleteProject(id);

      setProjects((prev) =>
        prev.filter((p) => p._id !== id)
      );

      setCurrentProject((prev) =>
        prev?._id === id ? null : prev
      );

      const saved = localStorage.getItem(storageKey);
      if (saved === id) {
        localStorage.removeItem(storageKey);
      }
    },
    [storageKey]
  );

  /* ===================================================== */
  /* 🔄 Refresh Current                                   */
  /* ===================================================== */

  const refreshCurrentProject = useCallback(async () => {
    if (!currentProject?._id) return null;

    try {
      const refreshed = await getProject(currentProject._id);

      setCurrentProject({
        ...refreshed,
        projectPlan: refreshed.plan || "free",
        isOwnerCompany:
          String(refreshed.clientCompanyId) === String(companyId),
        isContractorCompany:
          String(refreshed.contractorCompanyId) === String(companyId),
      });
      setProjects((prev) =>
        prev.map((p) =>
          p._id === refreshed._id ? refreshed : p
        )
      );

      return refreshed;
    } catch (err) {
      console.error("❌ Refresh failed:", err.message);
      return null;
    }
  }, [currentProject]);

  /* ===================================================== */
  /* 📦 Context Value (MEMOIZED)                          */
  /* ===================================================== */

  const value = useMemo(
    () => ({
      projects,
      currentProject,
      loading,

      selectProject,
      addProject,
      editProject,
      removeProject,
      refreshCurrentProject,

      reloadProjects: loadProjects,
    }),
    [
      projects,
      currentProject,
      loading,
      selectProject,
      addProject,
      editProject,
      removeProject,
      refreshCurrentProject,
      loadProjects,
    ]
  );

  /* ===================================================== */
  /* Render                                               */
  /* ===================================================== */

  return (
    <ProjectContext.Provider value={value}>
      {loading ? (
        <div className="p-8 text-center text-gray-500">
          Loading projects...
        </div>
      ) : (
        children
      )}
    </ProjectContext.Provider>
  );
};

/* ===================================================== */
/* Hook                                                  */
/* ===================================================== */

export const useProject = () => {
  const ctx = useContext(ProjectContext);

  if (!ctx) {
    throw new Error(
      "useProject must be used within ProjectProvider"
    );
  }

  return ctx;
};